import { faCalendarDays, faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    addDays,
    addMonths,
    format,
    getDay,
    isSameDay,
    isSameMonth,
    parseISO,
    startOfMonth,
    subDays,
    subMonths,
} from "date-fns";
import { useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { toIsoDateFromUkDate } from "./attendance-utils";

interface IAttendanceUkDateInputProps {
    calendarPlacement?: "above" | "below";
    disabled?: boolean;
    id: string;
    label: string;
    onChange: (value: string) => void;
    placeholder: string;
    testId?: string;
    value: string;
}

const weekdayLabels = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

export default function AttendanceUkDateInput(props: IAttendanceUkDateInputProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [displayedMonth, setDisplayedMonth] = useState(() => startOfMonth(new Date()));
    const selectedIsoDate = toIsoDateFromUkDate(props.value);
    const selectedDate = selectedIsoDate ? parseISO(selectedIsoDate) : null;
    const calendarDays = Array.from(
        { length: 42 },
        (_, index) => addDays(subDays(startOfMonth(displayedMonth), getDay(displayedMonth)), index),
    );

    const openCalendar = () => {
        if (selectedDate) {
            setDisplayedMonth(startOfMonth(selectedDate));
        }

        setIsCalendarOpen(true);
    };

    useEffect(() => {
        const closeCalendarWhenClickingOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsCalendarOpen(false);
            }
        };

        document.addEventListener("mousedown", closeCalendarWhenClickingOutside);
        return () => document.removeEventListener("mousedown", closeCalendarWhenClickingOutside);
    }, []);

    return (
        <div className="attendance-uk-date-input" ref={containerRef}>
            <Form.Control
                aria-label={props.label}
                aria-expanded={isCalendarOpen}
                aria-haspopup="dialog"
                autoComplete="off"
                data-testid={props.testId}
                disabled={props.disabled}
                id={props.id}
                inputMode="numeric"
                maxLength={10}
                onChange={(event) => props.onChange(event.target.value)}
                onClick={() => {
                    if (!props.disabled) {
                        openCalendar();
                    }
                }}
                onKeyDown={(event) => {
                    if (event.key === "Escape") {
                        setIsCalendarOpen(false);
                    }
                }}
                pattern="\d{2}/\d{2}/\d{4}"
                placeholder={props.placeholder}
                title="Enter a date as dd/mm/yyyy"
                type="text"
                value={props.value}
            />
            <button
                aria-label={`Open ${props.label} calendar`}
                className="attendance-uk-date-calendar-button"
                disabled={props.disabled}
                onClick={() => (isCalendarOpen ? setIsCalendarOpen(false) : openCalendar())}
                title={`Open ${props.label} calendar`}
                type="button"
            >
                <FontAwesomeIcon aria-hidden="true" icon={faCalendarDays} />
            </button>
            {isCalendarOpen && (
                <div
                    aria-label={`${props.label} calendar`}
                    className={`attendance-uk-date-calendar attendance-uk-date-calendar--${props.calendarPlacement ?? "below"}`}
                    role="dialog"
                >
                    <div className="attendance-uk-date-calendar-header">
                        <button
                            aria-label="Previous month"
                            className="attendance-uk-date-calendar-month-button"
                            onClick={() => setDisplayedMonth((month) => subMonths(month, 1))}
                            type="button"
                        >
                            <FontAwesomeIcon aria-hidden="true" icon={faChevronLeft} />
                        </button>
                        <strong>{format(displayedMonth, "MMMM yyyy")}</strong>
                        <button
                            aria-label="Next month"
                            className="attendance-uk-date-calendar-month-button"
                            onClick={() => setDisplayedMonth((month) => addMonths(month, 1))}
                            type="button"
                        >
                            <FontAwesomeIcon aria-hidden="true" icon={faChevronRight} />
                        </button>
                    </div>
                    <div className="attendance-uk-date-calendar-weekdays">
                        {weekdayLabels.map((weekday) => <span key={weekday}>{weekday}</span>)}
                    </div>
                    <div className="attendance-uk-date-calendar-days">
                        {calendarDays.map((day) => {
                            const isSelected = selectedDate ? isSameDay(day, selectedDate) : false;
                            const isToday = isSameDay(day, new Date());

                            return (
                                <button
                                    aria-label={format(day, "d MMMM yyyy")}
                                    className={`attendance-uk-date-calendar-day${isSameMonth(day, displayedMonth) ? "" : " is-outside-month"}${isSelected ? " is-selected" : ""}${isToday ? " is-today" : ""}`}
                                    key={format(day, "yyyy-MM-dd")}
                                    onClick={() => {
                                        props.onChange(format(day, "dd/MM/yyyy"));
                                        setIsCalendarOpen(false);
                                    }}
                                    type="button"
                                >
                                    {format(day, "d")}
                                </button>
                            );
                        })}
                    </div>
                    <div className="attendance-uk-date-calendar-actions">
                        <button
                            onClick={() => {
                                props.onChange("");
                                setIsCalendarOpen(false);
                            }}
                            type="button"
                        >
                            Clear
                        </button>
                        <button
                            onClick={() => {
                                props.onChange(format(new Date(), "dd/MM/yyyy"));
                                setIsCalendarOpen(false);
                            }}
                            type="button"
                        >
                            Today
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}